import { useApolloClient, useQuery } from '@apollo/client';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Heading,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  useToast,
} from '@eluve/components';
import { useTenantIdFromSession } from '@eluve/session-helpers';
import { useUploadUserFile } from '@eluve/smart-blocks';
import {
  type DeletedCode,
  type ParsedCode,
  getCptCategory,
  parseCpt,
  parseHcpcs,
  parseHistoricallyDeleted,
  parseIcd10,
  parseIcd10AllCodes,
  parseMbs,
} from '@eluve/utils';

import {
  deleteMedicalCodesByIdsMutation,
  deprecateMedicalCodesMutation,
  getDeprecatedCodesByTypeQuery,
  getExistingCodesQuery,
  getLastUpdatedQuery,
  insertMedicalCodesMutation,
  undoDeprecateMedicalCodesMutation,
} from './operations';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

const BillingCodesTabSchema = z
  .enum(['CPT', 'HCPCS', 'ICD_10', 'MBS'])
  .nullable()
  .transform((val) => val ?? 'CPT');

type BillingCodesTab = z.infer<typeof BillingCodesTabSchema>;

type DiffResult = {
  added: ParsedCode[];
  /** Codes that exist in the DB as deprecated but reappear in the source file — should be reactivated. */
  reactivated: ParsedCode[];
  unchanged: number;
  /** Codes in DB that are absent from the source file entirely — genuine CMS deletions. */
  removedFromSource: ParsedCode[];
  /** Codes in DB that appear in the source file but with billable=0 — should never have been stored. */
  nonBillableInDb: ParsedCode[];
  existingCount: number;
};

type UndoData = {
  insertedIds: string[];
  insertedCodes: string[];
  deprecatedCodes: string[];
  reactivatedCodes: string[];
  codeType: BillingCodesTab;
  pushedAt: string;
};

type UploadState =
  | { status: 'idle' }
  | { status: 'parsing' }
  | {
      status: 'diffing';
      fileName: string;
      codes: ParsedCode[];
      dbFetched: number;
    }
  | {
      status: 'diffed';
      fileName: string;
      file: File;
      codes: ParsedCode[];
      diff: DiffResult;
    }
  | {
      status: 'processing';
      inserted: number;
      totalInsert: number;
      deprecated: number;
      totalDeprecate: number;
    }
  | {
      status: 'done';
      fileName: string;
      addedCount: number;
      deprecatedCount: number;
      pushedAt: string;
    }
  | { status: 'undoing' }
  | { status: 'undone'; addedCount: number; deprecatedCount: number }
  | { status: 'error'; message: string };

// ---------------------------------------------------------------------------
// Diff display
// ---------------------------------------------------------------------------

type DiffFilter = 'all' | 'new' | 'removed' | 'nonBillable';

const DiffBox: React.FC<{
  diff: DiffResult;
  codeType: BillingCodesTab;
  deletedCodes: Map<string, DeletedCode> | null;
  getCategory?: (code: string) => string;
  showStatusColumn?: boolean;
}> = ({
  diff,
  codeType,
  deletedCodes,
  getCategory,
  showStatusColumn = false,
}) => {
  const [filter, setFilter] = useState<DiffFilter>('all');

  const confirmedCount = deletedCodes
    ? diff.removedFromSource.filter((c) => deletedCodes.has(c.code)).length
    : null;
  const unknownCount =
    confirmedCount !== null
      ? diff.removedFromSource.length - confirmedCount
      : null;

  const showNew = filter === 'all' || filter === 'new';
  const showRemoved = filter === 'all' || filter === 'removed';
  const showNonBillable = filter === 'all' || filter === 'nonBillable';

  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);

  const matchesSearch = (c: ParsedCode, changeType: string) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.code.toLowerCase().includes(q) ||
      c.description.toLowerCase().includes(q) ||
      changeType.includes(q) ||
      (getCategory ? getCategory(c.code).toLowerCase().includes(q) : false) ||
      (deletedCodes?.get(c.code)?.year?.includes(q) ?? false)
    );
  };

  const handleCopy = () => {
    const headers = [
      'Change',
      'Code',
      getCategory ? 'Category' : null,
      showStatusColumn ? 'Status' : null,
      'Description',
    ]
      .filter(Boolean)
      .join('\t');

    const rows: string[] = [headers];

    if (showNew) {
      for (const c of diff.added) {
        const cols = [
          '+',
          c.code,
          getCategory ? getCategory(c.code) : null,
          showStatusColumn ? '' : null,
          c.description,
        ]
          .filter((v) => v !== null)
          .join('\t');
        rows.push(cols);
      }
    }
    if (showRemoved) {
      for (const c of diff.removedFromSource) {
        const status = showStatusColumn
          ? deletedCodes?.get(c.code)
            ? `deprecated ${deletedCodes.get(c.code)!.year} (AMA)`
            : 'not in AMA deleted list'
          : null;
        const cols = [
          '−',
          c.code,
          getCategory ? getCategory(c.code) : null,
          status,
          c.description,
        ]
          .filter((v) => v !== null)
          .join('\t');
        rows.push(cols);
      }
    }
    if (showNonBillable) {
      for (const c of diff.nonBillableInDb) {
        const cols = [
          '≠',
          c.code,
          getCategory ? getCategory(c.code) : null,
          showStatusColumn ? '' : null,
          c.description,
        ]
          .filter((v) => v !== null)
          .join('\t');
        rows.push(cols);
      }
    }

    navigator.clipboard.writeText(rows.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const CategoryCell: React.FC<{ code: string }> = ({ code }) => {
    if (!getCategory) return null;
    const label = getCategory(code);
    return (
      <span
        style={{
          width: 90,
          flexShrink: 0,
          color: '#6b7280',
          fontStyle: 'italic',
        }}
      >
        {label}
      </span>
    );
  };

  const pillBase: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 10px',
    borderRadius: 999,
    border: '1.5px solid transparent',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.1s, border-color 0.1s',
    userSelect: 'none',
  };

  const pill = (
    id: DiffFilter,
    label: string,
    count: number,
    activeColor: string,
    activeBg: string,
    inactiveColor: string,
  ) => {
    const active = filter === id;
    return (
      <span
        onClick={() => setFilter(active ? 'all' : id)}
        style={{
          ...pillBase,
          background: active ? activeBg : 'transparent',
          borderColor: active ? activeColor : '#e5e7eb',
          color: active ? activeColor : inactiveColor,
        }}
      >
        {label}{' '}
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {count.toLocaleString()}
        </span>
      </span>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Filter pills — double as summary stats */}
      <div className="flex flex-wrap items-center gap-2">
        {pill(
          'new',
          '+ New',
          diff.added.length,
          '#15803d',
          '#dcfce7',
          '#6b7280',
        )}
        {pill(
          'removed',
          '− Deprecated',
          diff.removedFromSource.length,
          '#b91c1c',
          '#fee2e2',
          '#6b7280',
        )}
        {diff.nonBillableInDb.length > 0 &&
          pill(
            'nonBillable',
            '≠ Non-billable',
            diff.nonBillableInDb.length,
            '#7e22ce',
            '#f3e8ff',
            '#6b7280',
          )}
        <span className="pl-1 text-sm text-text-tertiary">
          {diff.unchanged.toLocaleString()} unchanged (
          {diff.existingCount.toLocaleString()} fetched from DB)
        </span>
        {filter !== 'all' &&
          diff.removedFromSource.length > 0 &&
          filter === 'removed' &&
          confirmedCount !== null && (
            <span className="text-xs text-text-secondary">
              {confirmedCount} confirmed by AMA
              {unknownCount ? `, ${unknownCount} unverified` : ''}
            </span>
          )}
        <span style={{ marginLeft: 'auto' }}>
          <Button onClick={handleCopy} variant="ghost" size="sm">
            {copied ? '✓ Copied' : 'Copy to clipboard'}
          </Button>
        </span>
      </div>

      <Input
        type="search"
        placeholder="Search codes, descriptions…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm"
      />

      <div className="h-72 overflow-x-auto overflow-y-auto rounded-md border border-border-secondary text-xs">
        {/* Header row */}
        <div
          className="flex items-center gap-3 border-b border-border-secondary px-3 py-1.5 font-semibold whitespace-nowrap text-text-secondary"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            background: '#f9fafb',
          }}
        >
          <span style={{ width: 16, flexShrink: 0 }} />
          <span style={{ width: 96, flexShrink: 0 }}>Code</span>
          {getCategory && (
            <span style={{ width: 90, flexShrink: 0 }}>Category</span>
          )}
          {showStatusColumn && (
            <span style={{ width: 160, flexShrink: 0 }}>Status</span>
          )}
          <span>Description</span>
        </div>

        {showNew &&
          diff.added
            .filter((c) => matchesSearch(c, 'new'))
            .map((c) => (
              <div
                key={`add-${c.code}`}
                className="flex items-center gap-3 border-b border-border-tertiary px-3 py-1.5 whitespace-nowrap"
              >
                <span
                  style={{
                    width: 16,
                    flexShrink: 0,
                    fontWeight: 700,
                    color: '#16a34a',
                  }}
                >
                  +
                </span>
                <span
                  style={{
                    width: 96,
                    flexShrink: 0,
                    fontFamily: 'monospace',
                    color: '#15803d',
                  }}
                >
                  {c.code}
                </span>
                <CategoryCell code={c.code} />
                {showStatusColumn && (
                  <span style={{ width: 160, flexShrink: 0 }} />
                )}
                <span
                  className="min-w-0 flex-1 truncate text-text-primary"
                  title={c.description}
                >
                  {c.description}
                </span>
              </div>
            ))}

        {showRemoved &&
          diff.removedFromSource
            .filter((c) => matchesSearch(c, 'removed'))
            .map((c) => {
              const deleted = deletedCodes?.get(c.code);
              return (
                <div
                  key={`rem-${c.code}`}
                  className="flex items-center gap-3 border-b border-border-tertiary px-3 py-1.5 whitespace-nowrap"
                >
                  <span
                    style={{
                      width: 16,
                      flexShrink: 0,
                      fontWeight: 700,
                      color: '#dc2626',
                    }}
                  >
                    −
                  </span>
                  <span
                    style={{
                      width: 96,
                      flexShrink: 0,
                      fontFamily: 'monospace',
                      color: '#b91c1c',
                    }}
                  >
                    {c.code}
                  </span>
                  <CategoryCell code={c.code} />
                  {showStatusColumn && (
                    <span style={{ width: 160, flexShrink: 0 }}>
                      {deleted ? (
                        <span
                          className="rounded-md px-1.5 py-0.5 font-medium"
                          style={{ background: '#dcfce7', color: '#166534' }}
                        >
                          ✓ deprecated {deleted.year} (AMA)
                        </span>
                      ) : (
                        <span
                          className="rounded-md px-1.5 py-0.5 font-medium"
                          style={{ background: '#fef3c7', color: '#92400e' }}
                        >
                          ⚠ not in AMA deleted list
                        </span>
                      )}
                    </span>
                  )}
                  <span
                    className="min-w-0 flex-1 truncate text-text-primary"
                    title={c.description}
                  >
                    {c.description}
                  </span>
                </div>
              );
            })}

        {showNonBillable &&
          diff.nonBillableInDb
            .filter((c) => matchesSearch(c, 'non-billable'))
            .map((c) => (
              <div
                key={`nb-${c.code}`}
                className="flex items-center gap-3 border-b border-border-tertiary px-3 py-1.5 whitespace-nowrap"
                style={{ opacity: 0.6 }}
              >
                <span
                  style={{
                    width: 16,
                    flexShrink: 0,
                    fontWeight: 700,
                    color: '#9333ea',
                  }}
                >
                  ≠
                </span>
                <span
                  style={{
                    width: 96,
                    flexShrink: 0,
                    fontFamily: 'monospace',
                    color: '#7e22ce',
                  }}
                >
                  {c.code}
                </span>
                <CategoryCell code={c.code} />
                {showStatusColumn && (
                  <span style={{ width: 160, flexShrink: 0 }} />
                )}
                <span
                  className="min-w-0 flex-1 truncate text-text-secondary"
                  title={c.description}
                >
                  {c.description}
                </span>
              </div>
            ))}

        {filter === 'all' && (
          <div className="px-3 py-2 text-text-tertiary">
            · {diff.unchanged.toLocaleString()} existing {codeType} codes
            unchanged
          </div>
        )}
      </div>

      <p className="text-xs text-text-tertiary">
        Deprecated codes are soft-deleted — marked inactive but never removed
        from the database.
      </p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Drop zone
// ---------------------------------------------------------------------------

const DropZone: React.FC<{
  accept: string;
  label: string;
  sublabel?: string;
  disabled?: boolean;
  hasError?: boolean;
  selectedFile: string | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({
  accept,
  label,
  sublabel,
  disabled,
  hasError,
  selectedFile,
  inputRef,
  onChange,
}) => {
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || !inputRef.current) return;
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const dt = new DataTransfer();
    dt.items.add(file);
    inputRef.current.files = dt.files;
    inputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
  };

  return (
    <div
      onClick={() => !disabled && inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${dragOver ? '#6366f1' : hasError ? '#ef4444' : selectedFile ? '#16a34a' : '#d1d5db'}`,
        background: dragOver
          ? '#eef2ff'
          : hasError
            ? '#fef2f2'
            : selectedFile
              ? '#f0fdf4'
              : '#fafafa',
        borderRadius: 8,
        padding: '20px 24px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'border-color 0.15s, background 0.15s',
        maxWidth: 480,
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={onChange}
        disabled={disabled}
        style={{ display: 'none' }}
      />
      {hasError && selectedFile ? (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span style={{ color: '#ef4444', fontSize: 18 }}>⚠</span>
            <Text size="sm" weight="medium" color="primary">
              {selectedFile}
            </Text>
          </div>
          <Text size="sm" color="tertiary">
            Wrong file or unrecognized format — try a different file
          </Text>
        </div>
      ) : selectedFile ? (
        <div className="flex items-center gap-2">
          <span style={{ color: '#16a34a', fontSize: 18 }}>✓</span>
          <Text size="sm" color="primary" weight="medium">
            {selectedFile}
          </Text>
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <Text size="sm" color="primary" weight="medium">
            {label}
          </Text>
          {sublabel && (
            <Text size="sm" color="tertiary">
              {sublabel}
            </Text>
          )}
          <Text size="sm" color="tertiary">
            Drop file here or click to browse
          </Text>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Upload panel
// ---------------------------------------------------------------------------

type PanelProps = {
  codeType: BillingCodesTab;
  accept: string;
  hint: string;
  parser: (text: string) => ParsedCode[] | null;
  showDeletedUpload?: boolean;
  dropZoneLayout?: 'vertical' | 'horizontal';
  /** Normalize a code before comparing against DB keys (e.g. strip dots for ICD-10). Does not affect display. */
  normalizeCodeForComparison?: (code: string) => string;
  /** Returns a category label for a code (e.g. getCptCategory). When provided, a Category column is shown. */
  getCategory?: (code: string) => string;
  /** Parses ALL codes from the file (billable + non-billable). Used to detect DB codes that are non-billable category codes rather than genuine CMS deletions. */
  parseAllCodes?: (text: string) => Set<string>;
};

const BATCH = 5000;

// Gov/AMA exports are often Windows-1252, not UTF-8 — file.text() would mangle
// non-ASCII (°C, em dashes) into U+FFFD. Try strict UTF-8, fall back to cp1252.
const readFileText = async (file: File): Promise<string> => {
  const buf = await file.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buf);
  } catch {
    return new TextDecoder('windows-1252').decode(buf);
  }
};

const CodeUploadPanel: React.FC<PanelProps> = ({
  codeType,
  accept,
  hint,
  parser,
  showDeletedUpload = false,
  dropZoneLayout = 'vertical',
  normalizeCodeForComparison = (c) => c,
  getCategory,
  parseAllCodes,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const deletedInputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<UploadState>({ status: 'idle' });
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [deletedFileName, setDeletedFileName] = useState<string | null>(null);
  const [deletedFileError, setDeletedFileError] = useState(false);
  const [deletedCodes, setDeletedCodes] = useState<Map<
    string,
    DeletedCode
  > | null>(null);
  const [pendingUndo, setPendingUndo] = useState<UndoData | null>(null);
  const [showDeprecateWarning, setShowDeprecateWarning] = useState(false);
  const client = useApolloClient();
  const toast = useToast();
  const tenantId = useTenantIdFromSession();
  const { uploadUserFileToGCS } = useUploadUserFile(tenantId ?? '');

  const { data: lastUpdatedData } = useQuery(getLastUpdatedQuery, {
    variables: { codeType },
  });
  const lastUpdated =
    lastUpdatedData?.medicalCodesAggregate?.aggregate?.max?.updatedAt;
  const totalCount = lastUpdatedData?.medicalCodesAggregate?.aggregate?.count;

  const undoKey = `billingCodes_undo_${codeType}`;

  useEffect(() => {
    const raw = localStorage.getItem(undoKey);
    if (raw) {
      try {
        setPendingUndo(JSON.parse(raw) as UndoData);
      } catch {
        /* ignore */
      }
    }
  }, [undoKey]);

  const fetchExistingCodes = async (
    onProgress: (fetched: number) => void,
  ): Promise<Map<string, string>> => {
    const existing = new Map<string, string>();
    let offset = 0;
    while (true) {
      const { data } = await client.query({
        query: getExistingCodesQuery,
        variables: { codeType, limit: BATCH, offset },
        fetchPolicy: 'network-only',
      });
      for (const row of data.medicalCodes)
        existing.set(row.code, row.description ?? '');
      offset += data.medicalCodes.length;
      onProgress(existing.size);
      if (data.medicalCodes.length < BATCH) break;
    }
    return existing;
  };

  const handleDeletedFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDeletedFileName(file.name);
    setDeletedFileError(false);
    const text = await readFileText(file);
    const parsed = parseHistoricallyDeleted(text);
    if (!parsed || parsed.size === 0) {
      setDeletedFileError(true);
    } else {
      setDeletedCodes(parsed);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFileName(file.name);

    setState({ status: 'parsing' });
    try {
      const text = await readFileText(file);
      const codes = parser(text);

      if (codes === null) {
        setState({
          status: 'error',
          message:
            'Wrong file format — make sure you uploaded the correct file for this code type.',
        });
        return;
      }

      if (codes.length === 0) {
        setState({
          status: 'error',
          message:
            'No codes found — make sure you uploaded the correct file for this code type.',
        });
        return;
      }

      setState({ status: 'diffing', fileName: file.name, codes, dbFetched: 0 });

      const existingCodes = await fetchExistingCodes((dbFetched) =>
        setState((prev) =>
          prev.status === 'diffing' ? { ...prev, dbFetched } : prev,
        ),
      );
      // Normalize both sides so dot-format differences (e.g. DB stores "F1599" vs parser "F15.99") don't cause false diffs
      const parsedByNorm = new Map(
        codes.map((c) => [normalizeCodeForComparison(c.code), c]),
      );
      const existingByNorm = new Map(
        [...existingCodes.entries()].map(([code, description]) => [
          normalizeCodeForComparison(code),
          { code, description },
        ]),
      );

      const added = codes.filter(
        (c) => !existingByNorm.has(normalizeCodeForComparison(c.code)),
      );
      const unchanged = codes.length - added.length;

      // Check which "added" codes are actually deprecated in the DB
      const deprecatedSet = new Set<string>();
      if (added.length > 0) {
        const { data: deprecatedData } = await client.query({
          query: getDeprecatedCodesByTypeQuery,
          variables: {
            codeType,
            codes: added.map((c) => c.code),
          },
          fetchPolicy: 'network-only',
        });
        for (const row of deprecatedData.medicalCodes) {
          deprecatedSet.add(row.code);
        }
      }

      const trulyAdded = added.filter((c) => !deprecatedSet.has(c.code));
      const reactivated = added.filter((c) => deprecatedSet.has(c.code));

      const allCodesInFile = parseAllCodes?.(text);
      const missingFromBillable = [...existingByNorm.entries()].filter(
        ([normCode]) => !parsedByNorm.has(normCode),
      );
      const removedFromSource: ParsedCode[] = [];
      const nonBillableInDb: ParsedCode[] = [];
      for (const [, { code, description }] of missingFromBillable) {
        const parsed = { code, description, longDescription: '' };
        if (allCodesInFile && allCodesInFile.has(code)) {
          nonBillableInDb.push(parsed);
        } else {
          removedFromSource.push(parsed);
        }
      }

      setState({
        status: 'diffed',
        fileName: file.name,
        file,
        codes,
        diff: {
          added: trulyAdded,
          reactivated,
          unchanged,
          removedFromSource,
          nonBillableInDb,
          existingCount: existingCodes.size,
        },
      });
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Failed to read file.',
      });
    }
  };

  const INSERT_BATCH = 500;
  const DEPRECATE_BATCH = 1000;

  const handleConfirmClick = () => {
    if (state.status !== 'diffed') return;
    const { diff } = state;
    const codesToDeprecate = [
      ...diff.removedFromSource,
      ...diff.nonBillableInDb,
    ];

    // Safety check: warn if more than 20% of existing codes would be deprecated
    if (
      diff.existingCount > 0 &&
      codesToDeprecate.length / diff.existingCount > 0.2
    ) {
      setShowDeprecateWarning(true);
      return;
    }

    handleConfirm();
  };

  const handleConfirm = async () => {
    setShowDeprecateWarning(false);
    if (state.status !== 'diffed') return;
    const { fileName, diff, file } = state;

    const codesToDeprecate = [
      ...diff.removedFromSource,
      ...diff.nonBillableInDb,
    ];

    setState({
      status: 'processing',
      inserted: 0,
      totalInsert: diff.added.length,
      deprecated: 0,
      totalDeprecate: codesToDeprecate.length,
    });

    // Save undo data incrementally so partial failures
    // can still be rolled back
    const insertedIds: string[] = [];
    const insertedCodes: string[] = [];
    const saveUndoProgress = () => {
      const undoData: UndoData = {
        insertedIds: [...insertedIds],
        insertedCodes: [...insertedCodes],
        deprecatedCodes: codesToDeprecate.map((c) => c.code),
        reactivatedCodes: diff.reactivated.map((c) => c.code),
        codeType,
        pushedAt: new Date().toISOString(),
      };
      localStorage.setItem(undoKey, JSON.stringify(undoData));
      setPendingUndo(undoData);
    };

    try {
      // 1. Insert new codes in batches
      for (let i = 0; i < diff.added.length; i += INSERT_BATCH) {
        const batch = diff.added.slice(i, i + INSERT_BATCH);
        const result = await client.mutate({
          mutation: insertMedicalCodesMutation,
          variables: {
            objects: batch.map((c) => ({
              code: c.code,
              description: c.description,
              longDescription: c.longDescription || c.description,
              codeType,
            })),
          },
        });
        for (const row of result.data?.insertMedicalCodes?.returning ?? []) {
          insertedIds.push(row.id);
        }
        for (const c of batch) {
          insertedCodes.push(c.code);
        }
        saveUndoProgress();
        setState((prev) =>
          prev.status === 'processing'
            ? {
                ...prev,
                inserted: Math.min(i + INSERT_BATCH, diff.added.length),
              }
            : prev,
        );
      }

      // 2. Reactivate deprecated codes that reappear in the source file
      if (diff.reactivated.length > 0) {
        for (let i = 0; i < diff.reactivated.length; i += DEPRECATE_BATCH) {
          const batch = diff.reactivated.slice(i, i + DEPRECATE_BATCH);
          await client.mutate({
            mutation: undoDeprecateMedicalCodesMutation,
            variables: { codes: batch.map((c) => c.code), codeType },
          });
        }
      }

      // 3. Deprecate removed + non-billable codes in batches
      for (let i = 0; i < codesToDeprecate.length; i += DEPRECATE_BATCH) {
        const batch = codesToDeprecate.slice(i, i + DEPRECATE_BATCH);
        await client.mutate({
          mutation: deprecateMedicalCodesMutation,
          variables: { codes: batch.map((c) => c.code), codeType },
        });
        setState((prev) =>
          prev.status === 'processing'
            ? {
                ...prev,
                deprecated: Math.min(
                  i + DEPRECATE_BATCH,
                  codesToDeprecate.length,
                ),
              }
            : prev,
        );
      }

      // 4. Final undo data
      saveUndoProgress();

      // Non-blocking: a failed file upload must not fail the code sync above.
      if (file && tenantId) {
        const onFailure = () =>
          toast.error(
            'Could not save the uploaded file to history. The code sync was still applied.',
          );
        try {
          const extension = file.name.split('.').pop() ?? 'txt';
          const userUploadId = await uploadUserFileToGCS({
            file,
            mimeType: 'text/plain',
            extension,
            fileType: 'BILLING_CODE',
          });
          if (!userUploadId) onFailure();
        } catch {
          onFailure();
        }
      }

      const pushedAt = new Date().toLocaleString();
      setState({
        status: 'done',
        fileName,
        addedCount: insertedIds.length,
        deprecatedCount: codesToDeprecate.length,
        pushedAt,
      });
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Failed to push codes.',
      });
    }
  };

  const handleUndo = async () => {
    const raw = localStorage.getItem(undoKey);
    if (!raw) return;
    let undoData: UndoData;
    try {
      undoData = JSON.parse(raw) as UndoData;
    } catch {
      return;
    }

    setState({ status: 'undoing' });

    try {
      // Delete inserted codes by ID
      for (let i = 0; i < undoData.insertedIds.length; i += INSERT_BATCH) {
        const batch = undoData.insertedIds.slice(i, i + INSERT_BATCH);
        await client.mutate({
          mutation: deleteMedicalCodesByIdsMutation,
          variables: { ids: batch },
        });
      }

      // Restore deprecated codes
      for (
        let i = 0;
        i < undoData.deprecatedCodes.length;
        i += DEPRECATE_BATCH
      ) {
        const batch = undoData.deprecatedCodes.slice(i, i + DEPRECATE_BATCH);
        await client.mutate({
          mutation: undoDeprecateMedicalCodesMutation,
          variables: { codes: batch, codeType },
        });
      }

      // Re-deprecate reactivated codes
      const reactivated = undoData.reactivatedCodes ?? [];
      for (let i = 0; i < reactivated.length; i += DEPRECATE_BATCH) {
        const batch = reactivated.slice(i, i + DEPRECATE_BATCH);
        await client.mutate({
          mutation: deprecateMedicalCodesMutation,
          variables: { codes: batch, codeType },
        });
      }

      localStorage.removeItem(undoKey);
      setPendingUndo(null);
      setState({
        status: 'undone',
        addedCount: undoData.insertedIds.length,
        deprecatedCount: undoData.deprecatedCodes.length,
      });
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Failed to undo.',
      });
    }
  };

  const handleReset = () => {
    setState({ status: 'idle' });
    setSelectedFileName(null);
    setDeletedFileName(null);
    setDeletedFileError(false);
    setDeletedCodes(null);
    if (inputRef.current) inputRef.current.value = '';
    if (deletedInputRef.current) deletedInputRef.current.value = '';
  };

  const busy =
    state.status === 'parsing' ||
    state.status === 'diffing' ||
    state.status === 'processing' ||
    state.status === 'undoing';

  return (
    <div className="flex flex-col gap-4 pt-4">
      <Text size="sm" color="secondary">
        {hint}
      </Text>
      {lastUpdated && (
        <Text size="sm" color="secondary">
          {totalCount?.toLocaleString()} codes in database · Last updated{' '}
          {new Date(lastUpdated).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </Text>
      )}

      {pendingUndo && state.status === 'idle' && (
        <Alert variant="warning" className="max-w-xl">
          <AlertTitle>
            Last push: {pendingUndo.insertedIds.length.toLocaleString()}{' '}
            inserted, {pendingUndo.deprecatedCodes.length.toLocaleString()}{' '}
            deprecated on {new Date(pendingUndo.pushedAt).toLocaleString()}
          </AlertTitle>
          <AlertDescription>
            <Button onClick={handleUndo} variant="ghost" size="sm">
              Undo
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div
        className={
          dropZoneLayout === 'horizontal'
            ? 'flex flex-row gap-3'
            : 'flex flex-col gap-3'
        }
      >
        <DropZone
          accept={accept}
          label="Drop code file here or click to browse"
          disabled={busy}
          hasError={state.status === 'error'}
          selectedFile={selectedFileName}
          inputRef={inputRef}
          onChange={handleFileChange}
        />

        {showDeletedUpload && (
          <DropZone
            accept=".txt"
            label="Historically Deleted Codes.txt (optional)"
            sublabel="Validates which removed codes are AMA-confirmed deletions"
            disabled={busy}
            hasError={deletedFileError}
            selectedFile={deletedFileName}
            inputRef={deletedInputRef}
            onChange={handleDeletedFileChange}
          />
        )}
      </div>

      {state.status === 'parsing' && (
        <Text size="sm" color="secondary">
          Reading file…
        </Text>
      )}

      {state.status === 'diffing' && (
        <Text size="sm" color="secondary">
          Fetched {state.dbFetched.toLocaleString()} existing codes from DB —
          comparing {state.codes.length.toLocaleString()} parsed codes…
        </Text>
      )}

      {state.status === 'error' && (
        <Alert variant="destructive" className="max-w-xl">
          <AlertTitle>Wrong file or unrecognized format</AlertTitle>
          <AlertDescription>
            <p>{state.message}</p>
            <div>
              <Button onClick={handleReset} variant="ghost" size="sm">
                Try a different file
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {state.status === 'diffed' && (
        <>
          <div className="text-sm text-text-primary">
            <span className="font-semibold">
              {state.codes.length.toLocaleString()}
            </span>{' '}
            {codeType} codes parsed from{' '}
            <span className="font-medium">{state.fileName}</span>
          </div>

          <DiffBox
            diff={state.diff}
            codeType={codeType}
            deletedCodes={deletedCodes}
            getCategory={getCategory}
            showStatusColumn={showDeletedUpload}
          />

          <div className="flex items-center gap-3">
            <Button
              onClick={handleConfirmClick}
              disabled={
                state.diff.added.length === 0 &&
                state.diff.reactivated.length === 0 &&
                state.diff.removedFromSource.length === 0 &&
                state.diff.nonBillableInDb.length === 0
              }
            >
              {[
                state.diff.added.length > 0 &&
                  `Insert ${state.diff.added.length.toLocaleString()} new`,
                state.diff.reactivated.length > 0 &&
                  `reactivate ${state.diff.reactivated.length.toLocaleString()}`,
                (state.diff.removedFromSource.length > 0 ||
                  state.diff.nonBillableInDb.length > 0) &&
                  `deprecate ${(state.diff.removedFromSource.length + state.diff.nonBillableInDb.length).toLocaleString()} removed`,
              ]
                .filter(Boolean)
                .join(' + ') || 'No changes'}
            </Button>
            <Button onClick={handleReset} variant="ghost">
              Cancel
            </Button>
          </div>
        </>
      )}

      {state.status === 'processing' && (
        <div className="max-w-xl rounded-md border border-border-info-subtle bg-bg-info-subtle px-4 py-3 text-sm text-text-info-subtle">
          {state.totalInsert > 0 && (
            <div>
              Inserting: {state.inserted.toLocaleString()} /{' '}
              {state.totalInsert.toLocaleString()}
            </div>
          )}
          {state.totalDeprecate > 0 && (
            <div>
              Deprecating: {state.deprecated.toLocaleString()} /{' '}
              {state.totalDeprecate.toLocaleString()}
            </div>
          )}
        </div>
      )}

      {state.status === 'done' && (
        <div className="max-w-xl rounded-md border border-border-positive-subtle bg-bg-positive-subtle px-4 py-3 text-sm text-text-positive-subtle">
          <div>
            <span className="font-semibold">Done</span> — pushed at{' '}
            {state.pushedAt}
          </div>
          <div className="mt-1 text-xs text-text-positive-subtle">
            {state.addedCount > 0 && (
              <span>{state.addedCount.toLocaleString()} codes inserted. </span>
            )}
            {state.deprecatedCount > 0 && (
              <span>
                {state.deprecatedCount.toLocaleString()} codes deprecated.
              </span>
            )}
          </div>
          <div className="mt-2 flex gap-2">
            <Button onClick={handleUndo} variant="ghost" size="sm">
              Undo
            </Button>
            <Button onClick={handleReset} variant="ghost" size="sm">
              Upload another
            </Button>
          </div>
        </div>
      )}

      {state.status === 'undoing' && (
        <div className="max-w-xl rounded-md border border-border-secondary bg-bg-secondary px-4 py-3 text-sm text-text-secondary">
          Reverting changes…
        </div>
      )}

      {state.status === 'undone' && (
        <div className="max-w-xl rounded-md border border-border-secondary bg-bg-secondary px-4 py-3 text-sm text-text-primary">
          <span className="font-semibold">Undone.</span>{' '}
          {state.addedCount > 0 && (
            <span>
              {state.addedCount.toLocaleString()} inserted codes deleted.{' '}
            </span>
          )}
          {state.deprecatedCount > 0 && (
            <span>
              {state.deprecatedCount.toLocaleString()} deprecated codes
              restored.
            </span>
          )}
          <div className="mt-2">
            <Button onClick={handleReset} variant="ghost" size="sm">
              Start over
            </Button>
          </div>
        </div>
      )}

      <Dialog
        open={showDeprecateWarning}
        onOpenChange={setShowDeprecateWarning}
      >
        <DialogContent
          size="lg"
          showCloseButton={false}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              {state.status === 'diffed' && (
                <>
                  This will deprecate{' '}
                  {(
                    state.diff.removedFromSource.length +
                    state.diff.nonBillableInDb.length
                  ).toLocaleString()}{' '}
                  codes (
                  {Math.round(
                    ((state.diff.removedFromSource.length +
                      state.diff.nonBillableInDb.length) /
                      state.diff.existingCount) *
                      100,
                  )}
                  % of existing). This may be caused by a truncated or wrong
                  file.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeprecateWarning(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Continue anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const BillingCodesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = BillingCodesTabSchema.parse(searchParams.get('tab'));

  const setTab = (newTab: BillingCodesTab) => {
    const params = new URLSearchParams();
    params.set('tab', newTab);
    setSearchParams(params, { replace: true });
  };

  return (
    <Tabs value={tab} className="w-full">
      <div className="flex w-full flex-row items-center justify-between">
        <Heading size="xl">Billing Codes</Heading>
        <TabsList>
          <TabsTrigger
            value="CPT"
            onClick={() => setTab('CPT')}
            className="px-8"
          >
            CPT
          </TabsTrigger>
          <TabsTrigger
            value="HCPCS"
            onClick={() => setTab('HCPCS')}
            className="px-8"
          >
            HCPCS
          </TabsTrigger>
          <TabsTrigger
            value="ICD_10"
            onClick={() => setTab('ICD_10')}
            className="px-8"
          >
            ICD-10
          </TabsTrigger>
          <TabsTrigger
            value="MBS"
            onClick={() => setTab('MBS')}
            className="px-8"
          >
            MBS
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="CPT" forceMount>
        <CodeUploadPanel
          codeType="CPT"
          accept=".txt"
          hint='Upload the AMA "Consolidated Code List.txt" from the Current Format > Primary Data Files folder.'
          parser={parseCpt}
          showDeletedUpload
          dropZoneLayout="horizontal"
          getCategory={getCptCategory}
        />
      </TabsContent>

      <TabsContent value="HCPCS" forceMount>
        <CodeUploadPanel
          codeType="HCPCS"
          accept=".txt"
          hint='Upload "HCPC2026_APR_ANWEB.txt" from the CMS HCPCS Level II annual release.'
          parser={parseHcpcs}
        />
      </TabsContent>

      <TabsContent value="ICD_10" forceMount>
        <CodeUploadPanel
          codeType="ICD_10"
          accept=".txt"
          hint="Upload icd10cm_order_YYYY.txt (CMS fixed-width format). Only billable leaf codes (flag=1) are included."
          parser={parseIcd10}
          parseAllCodes={parseIcd10AllCodes}
          normalizeCodeForComparison={(c) => c.replace('.', '')}
        />
      </TabsContent>

      <TabsContent value="MBS" forceMount>
        <CodeUploadPanel
          codeType="MBS"
          accept=".txt"
          hint="Upload the MBS Online Description file (e.g. 20260701_MBSONLINE_DESC.txt) from the MBS Downloads page."
          parser={parseMbs}
        />
      </TabsContent>
    </Tabs>
  );
};
